import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {HTMLElement | null} */
  #zoomedMediaContainer = null;

  /** @type {number | null} */
  #activePointerId = null;

  /** @type {HTMLElement | null} */
  #tapCandidateContainer = null;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragStartY = 0;

  /** @type {boolean} */
  #draggedWhileZoomed = false;

  /** @type {boolean} */
  #pointerDownOnZoomedMedia = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.addEventListener('pointerdown', this.#handleMobileZoomPointerDown, { signal, capture: true });
    this.addEventListener('pointermove', this.#handleMobileZoomPointerMove, { signal, capture: true });
    this.addEventListener('pointerup', this.#handleMobileZoomPointerUp, { signal, capture: true });
    this.addEventListener('pointercancel', this.#handleMobileZoomPointerCancel, { signal, capture: true });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resetInPlaceZoom();
    this.#controller.abort();
  }

  #isMobileZoomMode() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  /**
   * @param {EventTarget | null} target
   */
  #getZoomableImageContainer(target) {
    if (!(target instanceof Element)) return null;

    const container = target.closest('.product-media-container--zoomable');
    if (!(container instanceof HTMLElement) || !this.contains(container)) return null;

    const image = container.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return null;

    return container;
  }

  /**
   * @param {PointerEvent} event
   */
  #handleMobileZoomPointerDown = (event) => {
    if (!this.#isMobileZoomMode()) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const container = this.#getZoomableImageContainer(target);
    if (!(container instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    this.#activePointerId = event.pointerId;
    this.#dragStartX = event.clientX;
    this.#dragStartY = event.clientY;
    this.#draggedWhileZoomed = false;
    this.#tapCandidateContainer = container;
    this.#pointerDownOnZoomedMedia = this.#zoomedMediaContainer === container;
  };

  /**
   * @param {PointerEvent} event
   */
  #handleMobileZoomPointerMove = (event) => {
    if (!this.#isMobileZoomMode()) return;
    if (this.#activePointerId == null || event.pointerId !== this.#activePointerId) return;

    const deltaX = event.clientX - this.#dragStartX;
    const deltaY = event.clientY - this.#dragStartY;
    if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
      this.#draggedWhileZoomed = true;
    }

    const container = this.#zoomedMediaContainer;
    if (!(container instanceof HTMLElement) || !this.#pointerDownOnZoomedMedia) return;

    const image = container.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return;

    event.preventDefault();
    event.stopPropagation();
    this.#setZoomOriginFromPoint(container, image, event.clientX, event.clientY);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleMobileZoomPointerUp = (event) => {
    if (this.#activePointerId == null || event.pointerId !== this.#activePointerId) return;

    event.preventDefault();
    event.stopPropagation();

    const tapContainer = this.#tapCandidateContainer;

    if (this.#pointerDownOnZoomedMedia) {
      if (!this.#draggedWhileZoomed) {
        this.#resetInPlaceZoom();
        return;
      }

      this.#draggedWhileZoomed = false;
      this.#activePointerId = null;
      this.#tapCandidateContainer = null;
      this.#pointerDownOnZoomedMedia = false;
      return;
    }

    if (!this.#draggedWhileZoomed && tapContainer instanceof HTMLElement) {
      const image = tapContainer.querySelector('img');
      if (image instanceof HTMLImageElement) {
        this.#resetInPlaceZoom();
        this.#setZoomOriginFromPoint(tapContainer, image, event.clientX, event.clientY);
        tapContainer.classList.add('product-media-container--zoomed');
        this.#zoomedMediaContainer = tapContainer;
      }
    }

    this.#draggedWhileZoomed = false;
    this.#activePointerId = null;
    this.#tapCandidateContainer = null;
    this.#pointerDownOnZoomedMedia = false;
  };

  /**
   * @param {PointerEvent} event
   */
  #handleMobileZoomPointerCancel = (event) => {
    if (this.#activePointerId == null || event.pointerId !== this.#activePointerId) return;
    this.#draggedWhileZoomed = false;
    this.#activePointerId = null;
    this.#tapCandidateContainer = null;
    this.#pointerDownOnZoomedMedia = false;
  };

  /**
   * @param {HTMLElement} container
   * @param {HTMLImageElement} image
   * @param {number} clientX
   * @param {number} clientY
   */
  #setZoomOriginFromPoint(container, image, clientX, clientY) {
    const rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const originX = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    const originY = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));

    container.style.setProperty('--gallery-zoom-origin-x', `${originX}%`);
    container.style.setProperty('--gallery-zoom-origin-y', `${originY}%`);
  }

  #resetInPlaceZoom() {
    if (!(this.#zoomedMediaContainer instanceof HTMLElement)) {
      this.#activePointerId = null;
      return;
    }

    this.#zoomedMediaContainer.classList.remove('product-media-container--zoomed');
    this.#zoomedMediaContainer.style.removeProperty('--gallery-zoom-origin-x');
    this.#zoomedMediaContainer.style.removeProperty('--gallery-zoom-origin-y');
    this.#zoomedMediaContainer = null;
    this.#activePointerId = null;
    this.#draggedWhileZoomed = false;
    this.#tapCandidateContainer = null;
    this.#pointerDownOnZoomedMedia = false;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.#resetInPlaceZoom();
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
